import { Skeleton } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { useChurchActivityRows } from "@/features/finance/overview/lib/use-church-activity-rows"
import { formatMoneySafe } from "@/features/finance/overview/lib/overview-format"

/**
 * Which activities across the whole church made money, and which cost it.
 *
 * Activities are the church's events — a conference, a building drive, a youth camp — and several branches
 * often take part in the same one. Read at branch scope you see your slice; consolidated you see whether the
 * event worked at all, which is the level the decision to run it again is actually made at.
 *
 * Rows come from the consolidated breakdown the backend already returns. Losses are shown, not hidden: an
 * activity that cost more than it raised is the most useful row here.
 */
export function ChurchActivityFinanceWidget() {
  const t = useTranslations("finance.overview")
  const { data, query } = useChurchActivityRows()

  useWidgetMenu({ refresh: () => void query.refetch() })

  return (
    <WidgetBoundary
      isLoading={query.isLoading}
      isError={query.isError}
      isFetching={query.isFetching}
      data={data}
      isEmpty={(d) => d.rows.length === 0}
      skeleton={<ActivitySkeleton />}
      errorTitle={t("widgets.churchActivityFinance.error")}
      onRetry={() => void query.refetch()}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.churchActivityFinance.empty.title")}
    >
      {(d) => (
        <ul className='divide-border/60 divide-y'>
          {d.rows.map((row) => (
            <li key={row.name} className='flex items-center justify-between gap-3 py-1.5'>
              <span className='text-foreground min-w-0 flex-1 truncate text-sm'>{row.name}</span>
              <span
                className={
                  row.netIsNegative
                    ? "text-destructive shrink-0 text-sm font-medium tabular-nums"
                    : "shrink-0 text-sm font-medium tabular-nums text-emerald-600 dark:text-emerald-400"
                }
              >
                {formatMoneySafe(row.net, d.currency)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </WidgetBoundary>
  )
}

function ActivitySkeleton() {
  return (
    <div className='space-y-2'>
      {[0, 1, 2, 3].map((row) => (
        <div key={row} className='flex items-center justify-between py-1'>
          <Skeleton className='h-3.5 w-32' />
          <Skeleton className='h-3.5 w-16' />
        </div>
      ))}
    </div>
  )
}
